import { useNavigate, useSearch } from "@tanstack/react-router";
import { CalendarDays, ChevronLeft, ChevronRight, Filter, X } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DayPicker, type DateRange } from "react-day-picker";
import "react-day-picker/style.css";

import { useAuditQuery } from "@/api/query-hooks";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EmptyState, ErrorState, LoadingState } from "@/components/layout/route-states";
import { ClientScopePicker, useTenantClientScope } from "@/features/licensing/client-scope";
import { ClientIdentity } from "@/features/licensing/client-identity";
import { formatDateInput, formatDateTime, parseDateInput } from "@/lib/date";
import { formatRedactedJson } from "@/lib/redact";
import { positiveIntegerSearch } from "@/lib/search";

type AuditSearch = {
  event?: string;
  stationId?: string;
  appId?: string;
  clientId?: number;
  actor?: string;
  from?: string;
  to?: string;
  sort?: string;
  page?: string;
  pageSize?: string;
};

export function AuditPage() {
  const { t, i18n } = useTranslation(["audit", "common"]);
  const navigate = useNavigate({ from: "/audit" });
  const search = useSearch({ from: "/audit" });
  const params = useMemo(() => auditParams(search), [search]);
  const scope = useTenantClientScope(search.clientId);
  const query = useAuditQuery(params, scope.ready);
  const page = pageFromSearch(search.page);
  const pageSize = pageSizeFromSearch(search.pageSize);

  if (scope.isLoading || (scope.ready && query.isLoading)) {
    return <LoadingState />;
  }
  if (scope.isError) {
    return <ErrorState title={t("common:states.error")} detail={scope.error instanceof Error ? scope.error.message : undefined} onRetry={() => void scope.refetch()} />;
  }
  if (scope.ready && query.isError) {
    return <ErrorState title={t("common:states.error")} detail={query.error.message} onRetry={() => void query.refetch()} />;
  }

  const entries = query.data?.items ?? [];

  return (
    <div className="grid gap-4">
      <div>
        <h2 className="text-lg font-semibold">{t("title")}</h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("description")}</p>
      </div>
      {scope.tenant ? (
        <section className="rounded-md border bg-panel p-3">
          <ClientScopePicker
            value={search.clientId}
            required={scope.required}
            onChange={(clientId) => void navigate({ search: { ...search, clientId, page: undefined } })}
          />
        </section>
      ) : null}
      <section className="grid gap-3 rounded-md border bg-panel p-3 md:grid-cols-2 xl:grid-cols-8">
        <Field label={t("event")} value={search.event} onChange={(value) => void navigate({ search: { ...search, event: value || undefined, page: undefined } })} />
        <Field label="App" value={search.appId} onChange={(value) => void navigate({ search: { ...search, appId: value || undefined, page: undefined } })} />
        {!scope.tenant ? <Field label="Client" value={search.clientId ? String(search.clientId) : undefined} onChange={(value) => void navigate({ search: { ...search, clientId: positiveIntegerSearch(value), page: undefined } })} /> : null}
        <Field label={t("station")} value={search.stationId} onChange={(value) => void navigate({ search: { ...search, stationId: value || undefined, page: undefined } })} />
        <Field label={t("actor")} value={search.actor} onChange={(value) => void navigate({ search: { ...search, actor: value || undefined, page: undefined } })} />
        <DateRangeField
          from={search.from}
          to={search.to}
          onChange={(from, to) => void navigate({ search: { ...search, from: from || undefined, to: to || undefined, page: undefined } })}
        />
        <div className="grid gap-1.5">
          <Label className="flex items-center gap-1 text-xs text-muted-foreground">
            <Filter className="h-3 w-3" />
            {t("sort")}
          </Label>
          <div className="grid grid-cols-2 gap-1">
            {(["at_desc", "at_asc"] as const).map((sort) => (
              <Button
                key={sort}
                type="button"
                variant={(search.sort ?? "at_desc") === sort ? "default" : "outline"}
                onClick={() => void navigate({ search: { ...search, sort: sort === "at_desc" ? undefined : sort, page: undefined } })}
              >
                {t(sort)}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {entries.length === 0 ? (
        <EmptyState title={t("common:states.empty")} />
      ) : (
        <section className="overflow-hidden rounded-md border bg-panel">
          <div className="divide-y">
            {entries.map((entry) => (
              <details key={entry.id} className="group p-3">
                <summary className="grid cursor-pointer list-none gap-2 text-sm md:grid-cols-[180px_1fr_220px]">
                  <span className="text-muted-foreground">{formatDateTime(entry.at, i18n.language)}</span>
                  <span className="font-medium">{entry.event}</span>
                  <span className="flex min-w-0 items-center gap-1 text-muted-foreground"><span className="truncate">{entry.appId || "—"}</span><span>/</span><ClientIdentity clientId={entry.clientId} compact /></span>
                </summary>
                <pre className="mt-2 overflow-x-auto rounded bg-muted p-3 text-xs">{formatRedactedJson(entry.payload)}</pre>
              </details>
            ))}
          </div>
          <Pagination
            page={page}
            pageSize={pageSize}
            hasNext={entries.length === pageSize}
            onPage={(nextPage) => void navigate({ search: { ...search, page: nextPage > 1 ? String(nextPage) : undefined } })}
            onPageSize={(nextPageSize) => void navigate({ search: { ...search, pageSize: String(nextPageSize), page: undefined } })}
          />
        </section>
      )}
    </div>
  );
}

function auditParams(search: AuditSearch) {
  const page = pageFromSearch(search.page);
  const pageSize = pageSizeFromSearch(search.pageSize);
  const params = new URLSearchParams({ limit: String(pageSize), offset: String((page - 1) * pageSize) });
  for (const key of ["event", "stationId", "appId", "clientId", "actor"] as const) {
    if (search[key]) {
      params.set(key, String(search[key]));
    }
  }
  if (search.from) {
    params.set("since", search.from);
  }
  if (search.to) {
    params.set("until", search.to);
  }
  if (search.sort) {
    params.set("sort", search.sort);
  }
  return params;
}

function Pagination({
  page,
  pageSize,
  hasNext,
  onPage,
  onPageSize
}: {
  page: number;
  pageSize: number;
  hasNext: boolean;
  onPage: (page: number) => void;
  onPageSize: (pageSize: number) => void;
}) {
  const { t } = useTranslation(["audit", "common"]);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground">{t("pageSize")}</span>
        {[25, 50, 100].map((size) => (
          <Button key={size} size="sm" variant={pageSize === size ? "default" : "outline"} onClick={() => onPageSize(size)}>
            {size}
          </Button>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          <ChevronLeft className="h-4 w-4" />
          {t("previous")}
        </Button>
        <span className="min-w-16 text-center text-muted-foreground">{t("page")} {page}</span>
        <Button size="sm" variant="outline" disabled={!hasNext} onClick={() => onPage(page + 1)}>
          {t("next")}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function pageFromSearch(value: string | undefined) {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 1;
}

function pageSizeFromSearch(value: string | undefined) {
  const parsed = Number(value);
  return [25, 50, 100].includes(parsed) ? parsed : 50;
}

function Field({ label, value, type = "text", onChange }: { label: string; value?: string; type?: "date" | "text"; onChange: (value: string) => void }) {
  return (
    <div className="grid gap-1.5">
      <Label className="flex items-center gap-1 text-xs text-muted-foreground">
        <Filter className="h-3 w-3" />
        {label}
      </Label>
      <Input type={type} value={value ?? ""} onChange={(event) => onChange(event.target.value)} />
    </div>
  );
}

function DateRangeField({ from, to, onChange }: { from?: string; to?: string; onChange: (from: string, to: string) => void }) {
  const { t } = useTranslation(["audit", "common"]);
  const selected = useMemo<DateRange | undefined>(() => {
    const start = parseDateInput(from);
    const end = parseDateInput(to);
    return start || end ? { from: start, to: end } : undefined;
  }, [from, to]);
  const label = from || to ? `${from || "…"} – ${to || "…"}` : `${t("since")} / ${t("until")}`;

  return (
    <div className="grid gap-1.5 xl:col-span-2">
      <Label className="flex items-center gap-1 text-xs text-muted-foreground">
        <CalendarDays className="h-3 w-3" />
        {t("since")} / {t("until")}
      </Label>
      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
        <div className="grid grid-cols-2 gap-2">
          <Input type="date" value={from ?? ""} onChange={(event) => onChange(event.target.value, to ?? "")} />
          <Input type="date" value={to ?? ""} onChange={(event) => onChange(from ?? "", event.target.value)} />
        </div>
        <div className="flex gap-1">
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" className="w-full justify-start sm:w-44">
                <CalendarDays className="h-4 w-4" />
                <span className="truncate">{label}</span>
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="z-50 rounded-md border bg-popover p-3 text-popover-foreground shadow-md"
            >
              <DayPicker
                mode="range"
                numberOfMonths={1}
                selected={selected}
                onSelect={(range) => onChange(formatDateInput(range?.from), formatDateInput(range?.to))}
              />
            </PopoverContent>
          </Popover>
          {(from || to) ? (
            <Button type="button" variant="outline" size="icon" onClick={() => onChange("", "")}>
              <X className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
